import React, { useState, useEffect, useCallback } from 'react';
import {
  MdAdd,
  MdEdit,
  MdPeople,
  MdPersonRemove,
  MdSchool,
  MdClass,
  MdCalendarToday,
  MdDescription,
  MdSearch,
  MdFilterList,
  MdClose,
  MdSave,
  MdArchive,
  MdUnarchive,
  MdVisibility,
  MdCloudUpload,
  MdLock,
  MdPublic,
  MdPublicOff,
} from 'react-icons/md';
import { canManageClasses, canEnrollStudents } from '../../utils/permissions';

const ClassManagement = ({ 
  username, 
  userRole, 
  apiBaseUrl, 
  onNavigate 
}) => {
  // State management
  const [classes, setClasses] = useState([]);
  const [archivedClasses, setArchivedClasses] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdminClass, setSelectedAdminClass] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showArchivedDetailsModal, setShowArchivedDetailsModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Form state for creating/editing classes
  const [formData, setFormData] = useState({
    class_name: '',
    class_code: '',
    semester: '',
    year: new Date().getFullYear(),
    description: '',
    is_public: false
  });

  // Enrollment state
  const [enrollments, setEnrollments] = useState([]);

  // Auto-dismiss error messages after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Fetch all active classes
  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClasses(data.classes || []);
      } else {
        throw new Error('Failed to fetch classes');
      }
    } catch (err) {
      setError(`Error loading classes: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  // Fetch archived classes
  const fetchArchivedClasses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes/archived`, {
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        }
      });

      if (response.ok) {
        const data = await response.json();
        setArchivedClasses(data.classes || []);
      } else {
        throw new Error('Failed to fetch archived classes');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, userRole, username]);

  // Fetch class enrollments
  const fetchEnrollments = async (classId) => {
    try {
      const response = await fetch(`${apiBaseUrl}/classes/${classId}/enrollments`, {
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEnrollments(data.enrollments || []);
      } else {
        throw new Error('Failed to fetch enrollments');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch available users for enrollment

  // Create new class
  const createClass = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchClasses();
        setShowCreateModal(false);
        resetForm();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create class');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Update class
  const updateClass = async () => {
    try {
      setActionLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes/${selectedAdminClass.class_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchClasses();
        setShowEditModal(false);
        setSelectedAdminClass(null);
        resetForm();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update class');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Delete class (archive)
  const deleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to archive this class? It will be moved to archived classes.')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes/${classId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        }
      });

      if (response.ok) {
        await fetchClasses();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to archive class');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Unarchive class
  const unarchiveClass = async (classId) => {
    if (!window.confirm('Are you sure you want to restore this class? It will be moved back to active classes.')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes/${classId}/unarchive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        }
      });

      if (response.ok) {
        await fetchArchivedClasses();
        await fetchClasses();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to unarchive class');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle public status
  const togglePublicStatus = async (classId, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'make public' : 'make private';
    
    if (!window.confirm(`Are you sure you want to ${action} this class?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes/${classId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        body: JSON.stringify({ is_public: newStatus })
      });

      if (response.ok) {
        await fetchClasses();
        // Simple success message - class is now listed on landing page
        if (newStatus) {
          alert('Class is now public and will appear on the landing page.');
        } else {
          alert('Class is now private.');
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update public status');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Enroll users in class

  // Unenroll user from class
  const unenrollUser = async (usernameToRemove) => {
    if (!window.confirm(`Are you sure you want to remove ${usernameToRemove} from this class?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const classId = selectedAdminClass.class_id;
      const response = await fetch(`${apiBaseUrl}/classes/${classId}/unenroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Role': userRole,
          'Requesting-User': username
        },
        body: JSON.stringify({ username: usernameToRemove })
      });

      if (response.ok) {
        await fetchEnrollments(classId);
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to unenroll user');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle CSV file selection and upload
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('class_id', selectedAdminClass.class_id);

    try {
      setActionLoading(true);
      const response = await fetch(`${apiBaseUrl}/classes/bulk-enroll-csv`, {
        method: 'POST',
        headers: {
          'User-Role': userRole,
          'Requesting-User': username
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        const classId = selectedAdminClass.class_id;
        await fetchEnrollments(classId);
        
        // Show success message with details
        if (result.summary) {
          alert(`CSV Upload Complete:\n${result.summary.users_created} users created\n${result.summary.successful_enrollments} users enrolled\n${result.summary.failed_enrollments} failed`);
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to upload CSV');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      class_name: '',
      class_code: '',
      semester: '',
      year: new Date().getFullYear(),
      description: '',
      is_public: false
    });
    setSelectedAdminClass(null);
  };

  // Handle edit class
  const handleEditClass = (classData) => {
    setSelectedAdminClass(classData);
    setFormData({
      class_name: classData.class_name,
      class_code: classData.class_code,
      semester: classData.semester,
      year: classData.year,
      description: classData.description || '',
      is_public: classData.is_public || false
    });
    setShowEditModal(true);
  };

  // Handle manage enrollments
  const handleManageEnrollments = async (classData) => {
    setSelectedAdminClass(classData);
    await fetchEnrollments(classData.class_id);
    setShowEnrollmentModal(true);
  };

  // Filter classes based on search term
  const filteredClasses = (showArchived ? archivedClasses : classes).filter(cls => 
    (cls.class_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cls.class_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (cls.semester || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get available users for enrollment (not already enrolled)

  // Handle view toggle
  const handleViewToggle = (archived) => {
    setShowArchived(archived);
    setSearchTerm('');
    if (archived) {
      fetchArchivedClasses();
    } else {
      fetchClasses();
    }
  };

  // Load classes on component mount
  useEffect(() => {
    if (showArchived) {
      fetchArchivedClasses();
    } else {
      fetchClasses();
    }
  }, [showArchived, fetchClasses, fetchArchivedClasses]);

  // Check if user has permission to manage classes
  const canManage = canManageClasses(userRole);
  const canEnroll = canEnrollStudents(userRole);

  // Show access denied if user doesn't have permission (Admin only)
  if (!canManage) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <MdLock className="mx-auto text-gray-400 text-6xl mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to access class management.</p>
          <p className="text-gray-600 mt-2">Only Administrators can manage classes.</p>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {showArchived ? 'Archived Classes' : 'Class Management'}
          </h1>
          <p className="text-gray-600">
            {showArchived ? 'View and restore archived classes' : 'Manage classes and student enrollments'}
          </p>
        </div>
        <div className="flex gap-3">
          {!showArchived && (
            <button
              onClick={() => handleViewToggle(true)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <MdArchive />
              Archived Classes
            </button>
          )}
          {showArchived && (
            <button
              onClick={() => handleViewToggle(false)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <MdSchool />
              Active Classes
            </button>
          )}
          {!showArchived && canManage && (
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <MdAdd />
              Create Class
            </button>
          )}
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search classes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
          <MdFilterList className="text-gray-500" />
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <span className="text-red-800">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <MdClose />
          </button>
        </div>
      )}

      {/* Classes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClasses.map((cls) => (
          <div key={cls.class_id} className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <MdSchool className="text-blue-600 text-xl" />
                  <h3 className="text-lg font-semibold text-gray-900">{cls.class_name || 'Unknown Class'}</h3>
                </div>
                {cls.is_public && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-md">
                    <MdPublic className="text-blue-600 text-sm" />
                    <span className="text-xs text-blue-600 font-medium">Public</span>
                  </div>
                )}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MdClass />
                  <span>{cls.class_code || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MdCalendarToday />
                  <span>{cls.semester || 'N/A'} {cls.year || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MdPeople />
                  <span>{cls.enrollment_count || 0} students</span>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MdDescription />
                  <span className="truncate">{cls.description || 'None'}</span>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-200">
                {!showArchived ? (
                  // Active class buttons
                  <>
                    {canManage && (
                      <button
                        onClick={() => handleEditClass(cls)}
                        className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                      >
                        <MdEdit />
                        Edit
                      </button>
                    )}
                    {canEnroll && (
                      <button
                        onClick={() => handleManageEnrollments(cls)}
                        className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                      >
                        <MdPeople />
                        Enrollments
                      </button>
                    )}
                    {canManage && (
                      <button
                        onClick={() => togglePublicStatus(cls.class_id, cls.is_public || false)}
                        className={`px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                          cls.is_public
                            ? 'bg-green-50 hover:bg-green-100 text-green-700'
                            : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                        }`}
                        title={cls.is_public ? 'Make private' : 'Make public'}
                      >
                        {cls.is_public ? <MdPublicOff /> : <MdPublic />}
                      </button>
                    )}
                    {canManage && (
                      <button
                        onClick={() => deleteClass(cls.class_id)}
                        className="bg-red-50 hover:bg-red-100 text-red-700 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                      >
                        <MdArchive />
                      </button>
                    )}
                  </>
                ) : (
                  // Archived class buttons
                  <>
                    <button
                      onClick={() => {
                        setSelectedAdminClass(cls);
                        setShowArchivedDetailsModal(true);
                      }}
                      className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      <MdVisibility />
                      View Details
                    </button>
                    <button
                      onClick={() => unarchiveClass(cls.class_id)}
                      className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 px-3 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      <MdUnarchive />
                      Restore
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredClasses.length === 0 && !loading && (
        <div className="text-center py-12">
          <MdSchool className="mx-auto text-gray-400 text-6xl mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {showArchived ? 'No archived classes' : 'No classes found'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm 
              ? 'No classes match your search criteria.' 
              : showArchived 
                ? 'No classes have been archived yet.' 
                : 'Get started by creating your first class.'
            }
          </p>
          {!searchTerm && !showArchived && canManage && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Create Class
            </button>
          )}
        </div>
      )}

      {/* Create Class Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Create New Class</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <MdClose />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); createClass(); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Class Name *
                  </label>
                  <input
                    type="text"
                    value={formData.class_name}
                    onChange={(e) => setFormData({...formData, class_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Class Code *
                  </label>
                  <input
                    type="text"
                    value={formData.class_code}
                    onChange={(e) => setFormData({...formData, class_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., CS411-F25"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Semester *
                    </label>
                    <select
                      value={formData.semester}
                      onChange={(e) => setFormData({...formData, semester: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select...</option>
                      <option value="Fall">Fall</option>
                      <option value="Spring">Spring</option>
                      <option value="Summer">Summer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Year *
                    </label>
                    <input
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="2020"
                      max="2030"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Optional description..."
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="is_public_create"
                    checked={formData.is_public}
                    onChange={(e) => setFormData({...formData, is_public: e.target.checked})}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_public_create" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MdPublic className="text-gray-500" />
                    Make this class public
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <MdSave />
                    )}
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Class Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Edit Class</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <MdClose />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); updateClass(); }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Class Name *
                  </label>
                  <input
                    type="text"
                    value={formData.class_name}
                    onChange={(e) => setFormData({...formData, class_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Class Code *
                  </label>
                  <input
                    type="text"
                    value={formData.class_code}
                    onChange={(e) => setFormData({...formData, class_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Semester *
                    </label>
                    <select
                      value={formData.semester}
                      onChange={(e) => setFormData({...formData, semester: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select...</option>
                      <option value="Fall">Fall</option>
                      <option value="Spring">Spring</option>
                      <option value="Summer">Summer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Year *
                    </label>
                    <input
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="2020"
                      max="2030"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="is_public_edit"
                    checked={formData.is_public}
                    onChange={(e) => setFormData({...formData, is_public: e.target.checked})}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_public_edit" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MdPublic className="text-gray-500" />
                    Make this class public
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <MdSave />
                    )}
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Enrollment Management Modal */}
      {showEnrollmentModal && selectedAdminClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold">Manage Enrollments</h2>
                  <p className="text-gray-600">{selectedAdminClass.class_name} ({selectedAdminClass.class_code})</p>
                </div>
                <button
                  onClick={() => setShowEnrollmentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <MdClose />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Enrollments */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Current Enrollments ({enrollments.length})</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {enrollments.map((enrollment, index) => (
                      <div key={`${enrollment.username}-${index}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-medium text-sm">
                              {enrollment.firstname?.[0] || enrollment.username?.[0]?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {enrollment.firstname || 'Unknown'} {enrollment.lastname || 'User'}
                            </p>
                            <p className="text-sm text-gray-600">{enrollment.username} - {enrollment.role_name || 'Unknown Role'}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => unenrollUser(enrollment.username)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Remove from class"
                        >
                          <MdPersonRemove />
                        </button>
                      </div>
                    ))}
                    {enrollments.length === 0 && (
                      <p className="text-gray-500 text-center py-8">No students enrolled yet</p>
                    )}
                  </div>
                </div>

                {/* CSV Upload Only */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium">Add Users via CSV</h3>
                  </div>
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <MdCloudUpload className="mx-auto text-4xl text-gray-400 mb-2" />
                      <p className="text-gray-600 mb-2">Upload a CSV file to add users to this class</p>
                      <p className="text-sm text-gray-500 mb-4">
                        CSV Format: username,firstname,lastname,role_id<br/>
                        Role IDs: 1=Student, 2=TA, 3=Instructor, 4=Admin
                      </p>
                    </div>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileSelect}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={actionLoading}
                      />
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-colors disabled:opacity-50"
                        disabled={actionLoading}
                      >
                        {actionLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <MdCloudUpload />
                        )}
                        {actionLoading ? 'Uploading...' : 'Upload CSV File'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archived Class Details Modal */}
      {showArchivedDetailsModal && selectedAdminClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Archived Class Details</h2>
                <button
                  onClick={() => setShowArchivedDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <MdClose />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <MdSchool className="text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Class Name</p>
                    <p className="font-medium">{selectedAdminClass.class_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <MdClass className="text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Class Code</p>
                    <p className="font-medium">{selectedAdminClass.class_code}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <MdCalendarToday className="text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Semester & Year</p>
                    <p className="font-medium">{selectedAdminClass.semester} {selectedAdminClass.year}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <MdPeople className="text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Students Enrolled</p>
                    <p className="font-medium">{selectedAdminClass.enrollment_count || 0}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <MdDescription className="text-gray-500 mt-1" />
                  <div>
                    <p className="text-sm text-gray-600">Description</p>
                    <p className="font-medium">{selectedAdminClass.description || 'None'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <MdArchive className="text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <p className="font-medium text-orange-600">Archived</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={() => setShowArchivedDetailsModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowArchivedDetailsModal(false);
                    unarchiveClass(selectedAdminClass.class_id);
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <MdUnarchive />
                  Restore Class
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClassManagement;